import { useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Store, LogIn, Lock, Mail, Shield } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [is2faStep, setIs2faStep] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);

  const { login, verify2FA } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await login(email, password);
      if (res.require2FA) {
        setIs2faStep(true);
        setPendingUserId(res.userId);
      } else {
        navigate('/app');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials');
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await verify2FA(pendingUserId, twoFactorCode);
      navigate('/app');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid 2FA code');
    }
  };

  if (is2faStep) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden" style={{ backgroundColor: 'var(--bg-main)' }}>
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--brand)', opacity: 0.06 }}></div>
        <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
          <div className="flex justify-center">
            <div className="p-4 rounded-2xl text-white shadow-2xl" style={{ backgroundColor: 'var(--brand)' }}>
              <Shield size={40} />
            </div>
          </div>
          <h2 className="mt-8 text-center text-3xl font-black tracking-tight" style={{ color: 'var(--text-main)' }}>Two-Factor Auth</h2>
          <p className="mt-2 text-center text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Enter the code from your Authenticator app</p>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
          <div className="py-10 px-6 shadow-2xl sm:rounded-2xl sm:px-10" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <form className="space-y-6" onSubmit={handleVerify2FA}>
              {error && <div className="bg-rose-500/10 text-rose-500 p-4 rounded-xl text-xs font-black border border-rose-500/20 text-center uppercase tracking-widest">{error}</div>}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest mb-2 text-center" style={{ color: 'var(--text-muted)' }}>6-digit Verification Code</label>
                <input
                  required
                  autoFocus
                  type="text"
                  maxLength="6"
                  className="block w-full sm:text-3xl text-center rounded-2xl py-4 tracking-[0.5em] font-black outline-none transition-all"
                  style={{ backgroundColor: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                  placeholder="000000"
                  value={twoFactorCode}
                  onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <button type="submit" className="w-full flex justify-center py-4 px-4 rounded-xl shadow-lg text-xs font-black text-white uppercase tracking-widest transition-all" style={{ backgroundColor: 'var(--brand)' }}>
                Verify & Sign In
              </button>
              <button type="button" onClick={() => setIs2faStep(false)} className="w-full text-xs font-bold uppercase tracking-widest transition-colors" style={{ color: 'var(--text-muted)' }}>
                Back to Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden" style={{ backgroundColor: 'var(--bg-main)' }}>
      {/* Decorative background elements */}
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--brand)', opacity: 0.06 }}></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--brand)', opacity: 0.06 }}></div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="flex justify-center">
          <div className="p-4 rounded-2xl text-white shadow-2xl" style={{ backgroundColor: 'var(--brand)', boxShadow: '0 0 60px var(--brand)' }}>
            <Store size={40} />
          </div>
        </div>
        <h2 className="mt-8 text-center text-3xl font-black tracking-tight" style={{ color: 'var(--text-main)' }}>
          Welcome to NexusPOS
        </h2>
        <p className="mt-2 text-center text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
          Sign in to your account
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="py-10 px-6 shadow-2xl sm:rounded-2xl sm:px-10" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <form className="space-y-6" onSubmit={handleLogin}>
            {error && (
              <div className="bg-rose-500/10 text-rose-500 p-4 rounded-xl text-xs font-black border border-rose-500/20 text-center uppercase tracking-widest">
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Email address</label>
              <div className="relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />
                </div>
                <input
                  type="email"
                  required
                  className="block w-full pl-12 sm:text-sm rounded-xl py-3 outline-none transition-all focus:ring-2"
                  style={{ backgroundColor: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)', '--tw-ring-color': 'var(--brand)' }}
                  placeholder="admin@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Password</label>
              <div className="relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />
                </div>
                <input
                  type="password"
                  required
                  className="block w-full pl-12 sm:text-sm rounded-xl py-3 outline-none transition-all focus:ring-2"
                  style={{ backgroundColor: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)', '--tw-ring-color': 'var(--brand)' }}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input id="remember-me" type="checkbox" className="h-4 w-4 rounded" style={{ accentColor: 'var(--brand)' }} />
                <label htmlFor="remember-me" className="block text-xs font-bold" style={{ color: 'var(--text-main)' }}>Remember me</label>
              </div>
              <div className="text-xs">
                <a href="#" className="font-bold transition-colors" style={{ color: 'var(--brand)' }}>Forgot password?</a>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-3.5 px-4 rounded-xl shadow-lg text-sm font-black text-white transition-all gap-2 items-center uppercase tracking-widest"
                style={{ backgroundColor: 'var(--brand)' }}
              >
                <LogIn size={18} />
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}